import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';

// Query-parameternamen die nooit naar Sentry mogen (naast het volledige
// hash-fragment, dat altijd gestript wordt — Supabase recovery-tokens en
// vergelijkbare gevoelige waarden staan doorgaans in #hash, maar sommige
// flows kunnen ze ook als query-param meesturen).
const SENSITIVE_QUERY_PARAMS = ['token', 'access_token', 'refresh_token', 'code', 'apikey', 'api_key'];

// Route-patronen met een gevoelig pad-segment (identifier in het URL-pad
// zelf, dus niet gedekt door de hash-/query-stripping hierboven) —
// afgeleid van web/src/App.jsx. Elke entry heeft een `reason`, want niet
// elk pad-segment is een even ernstig risico:
//   'security' — het segment IS zelf een bruikbaar geheim: bezit van de
//                waarde geeft toegang (zoals het agreement-token in
//                /overeenkomst/:token — functioneel vergelijkbaar met het
//                recovery-token dat al via het hash-fragment wordt
//                gestript bij /reset-wachtwoord).
//   'hygiene'  — het segment is een interne database-UUID
//                (invoiceId/clientId/lessonId/recipe-id). Geen bruikbaar
//                geheim op zichzelf (de route zit al achter auth), maar
//                interne ID's horen niet nodeloos in een Sentry-dashboard
//                te staan.
// Nieuwe route met een gevoelig pad-segment? Hier een entry toevoegen —
// geen wijziging elders nodig, precies zoals SENSITIVE_QUERY_PARAMS.
const REDACTED_PATH_SEGMENTS = [
  { pattern: /^\/overeenkomst\/[^/]+$/, replacement: '/overeenkomst/:token', reason: 'security' },
  { pattern: /^\/betaling-bevestiging\/[^/]+$/, replacement: '/betaling-bevestiging/:invoiceId', reason: 'hygiene' },
  { pattern: /^\/coach\/les\/[^/]+\/bewerken$/, replacement: '/coach/les/:lessonId/bewerken', reason: 'hygiene' },
  { pattern: /^\/coach\/clients\/[^/]+$/, replacement: '/coach/clients/:id', reason: 'hygiene' },
  { pattern: /^\/coach\/recepten\/[^/]+\/bewerken$/, replacement: '/coach/recepten/:id/bewerken', reason: 'hygiene' },
  { pattern: /^\/coach\/overeenkomst\/opstellen\/[^/]+$/, replacement: '/coach/overeenkomst/opstellen/:clientId', reason: 'hygiene' },
  { pattern: /^\/client\/voeding\/recept\/[^/]+$/, replacement: '/client/voeding/recept/:id', reason: 'hygiene' },
];

function redactPathSegments(pathname) {
  if (typeof pathname !== 'string') return pathname;
  const match = REDACTED_PATH_SEGMENTS.find(({ pattern }) => pattern.test(pathname));
  return match ? match.replacement : pathname;
}

// Strip het hash-fragment (bv. #access_token=...&type=recovery van de
// wachtwoord-reset-link), bekende gevoelige query-parameters, én bekende
// gevoelige pad-segmenten (zie REDACTED_PATH_SEGMENTS hierboven) uit een
// URL-string, generiek voor alle routes, voordat Sentry iets vastlegt.
function stripSensitiveUrlParts(url) {
  if (typeof url !== 'string' || !url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.hash = '';
    SENSITIVE_QUERY_PARAMS.forEach((key) => parsed.searchParams.delete(key));
    parsed.pathname = redactPathSegments(parsed.pathname);
    return parsed.toString();
  } catch {
    // Geen geldige absolute/relatieve URL (bv. al een pad zonder origin) —
    // strip in ieder geval hash en pad-segment met een regex-fallback.
    const withoutHash = url.replace(/#.*$/, '');
    const [pathPart, queryPart] = withoutHash.split('?');
    const redactedPath = redactPathSegments(pathPart);
    return queryPart ? `${redactedPath}?${queryPart}` : redactedPath;
  }
}

if (import.meta.env.VITE_SENTRY_DSN_WEB) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN_WEB,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay's navigatie-/netwerk-breadcrumbs lopen via dezelfde globale
      // breadcrumb-pipeline als de rest van de SDK, dus de beforeBreadcrumb-
      // hook hieronder strip ook daar het hash-fragment en gevoelige query-
      // parameters uit — geen aparte Replay-optie nodig.
      Sentry.replayIntegration(),
    ],
    // Testfase: 100% sampling (verlagen vóór livegang)
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.data) {
        if (typeof breadcrumb.data.url === 'string') {
          breadcrumb.data.url = stripSensitiveUrlParts(breadcrumb.data.url);
        }
        if (typeof breadcrumb.data.to === 'string') {
          breadcrumb.data.to = stripSensitiveUrlParts(breadcrumb.data.to);
        }
        if (typeof breadcrumb.data.from === 'string') {
          breadcrumb.data.from = stripSensitiveUrlParts(breadcrumb.data.from);
        }
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = stripSensitiveUrlParts(event.request.url);
      }
      if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs.forEach((breadcrumb) => {
          if (breadcrumb?.data) {
            if (typeof breadcrumb.data.url === 'string') {
              breadcrumb.data.url = stripSensitiveUrlParts(breadcrumb.data.url);
            }
            if (typeof breadcrumb.data.to === 'string') {
              breadcrumb.data.to = stripSensitiveUrlParts(breadcrumb.data.to);
            }
            if (typeof breadcrumb.data.from === 'string') {
              breadcrumb.data.from = stripSensitiveUrlParts(breadcrumb.data.from);
            }
          }
        });
      }
      return event;
    },
    beforeSendTransaction(event) {
      if (event.request?.url) {
        event.request.url = stripSensitiveUrlParts(event.request.url);
      }
      return event;
    },
  });
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </StrictMode>
);
